// src/pages/dashboard/components/FinancialSummary.jsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  CircularProgress, 
  Divider, 
  IconButton, 
  Tooltip, 
  useTheme, 
  alpha 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  ResponsiveContainer, 
  Tooltip as RechartsTooltip, 
  Legend, 
  CartesianGrid 
} from 'recharts';
import { 
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  RemoveRedEye as ViewIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
}));

// Custom tooltip for chart
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 1.5,
          boxShadow: '0 3px 14px rgba(0,0,0,0.15)',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {label}
        </Typography>
        
        {payload.map((entry, index) => (
          <Box 
            key={`tooltip-${index}`} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 0.5
            }}
          >
            <Box 
              sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                bgcolor: entry.color,
                mr: 1
              }} 
            />
            <Typography variant="body2">
              {entry.name}: ${entry.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  
  return null;
};

// Stat card component
const StatCard = ({ title, value, change, icon, onClick }) => {
  const theme = useTheme();
  const isPositive = change >= 0;
  
  return (
    <StyledPaper elevation={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            ${value.toLocaleString()}
          </Typography>
          
          {change !== undefined && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 0.5,
              color: isPositive ? theme.palette.success.main : theme.palette.error.main
            }}>
              {isPositive ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {isPositive ? '+' : ''}{change}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={onClick}
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main
              }
            }}
          >
            {icon || <ViewIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </StyledPaper>
  );
};

const FinancialSummary = ({ data = {} }) => {
  const theme = useTheme();
  
  // Prepare chart data
  const revenueByMonth = data.revenueByMonth || [];
  
  // Check if data is available
  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No financial data available
        </Typography>
      </Box>
    );
  }
  
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Financial Overview
          </Typography>
          <Tooltip title="Financial data includes invoiced amounts and campaign budgets">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
      
      {/* Financial stats */}
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Revenue"
          value={data.totalPaid || 0}
          change={data.revenueChange}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Average Invoice"
          value={data.averageInvoice || 0}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Pending Invoices"
          value={data.pendingAmount || 0}
          change={data.pendingChange}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Campaign Budget"
          value={data.campaignBudget || 0}
          change={data.budgetChange}
        />
      </Grid>
      
      {/* Revenue chart */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Monthly Revenue
        </Typography>
        
        <Box sx={{ width: '100%', height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={revenueByMonth}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
              <XAxis 
                dataKey="month" 
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => `$${value}`}
              />
              <RechartsTooltip content={<CustomChartTooltip />} />
              <Legend />
              <Bar 
                dataKey="revenue" 
                name="Revenue" 
                fill={theme.palette.success.main} 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="costs" 
                name="Costs" 
                fill={theme.palette.error.main} 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
      
      {/* Financial details */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Total invoices: {data.invoiceCount || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg. payment time: {data.averagePaymentDays || 0} days
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default FinancialSummary;